<template>
  <div class="transaction-list-container">
    <div class="card">
      <h2>交易记录</h2>
      
      <div class="filters mb-4">
        <div class="filter-row">
          <el-select v-model="filters.transType" placeholder="交易类型" class="filter-item">
            <el-option label="全部类型" :value="0" />
            <el-option label="充值" :value="1" />
            <el-option label="使用" :value="2" />
            <el-option label="赠送" :value="3" />
          </el-select>
          <el-input v-model="filters.orderNo" placeholder="订单号" class="filter-item" />
          <el-button type="primary" @click="loadTransactions" class="filter-item">
            <el-icon><Search /></el-icon>
            查找
          </el-button>
        </div>
      </div>

      <p v-if="refundConfig.complaintPhone" class="complaint-hint">
        消费订单可在 {{ refundConfig.appealDays || 3 }} 天内申诉退款；投诉热线：
        <a :href="'tel:' + refundConfig.complaintPhone">{{ refundConfig.complaintPhone }}</a>
      </p>

      <el-table
        :data="transactions"
        style="width: 100%"
        border
        v-loading="loading"
      >
        <el-table-column
          prop="createdAt"
          label="时间"
          width="180"
        >
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="payType"
          label="类型"
          width="120"
        >
          <template #default="{ row }">
            <el-tag :type="getTypeTagType(row.payType)">
              {{ row.payType }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          prop="orderNo"
          label="订单号"
          width="180"
        />
        <el-table-column
          prop="detail"
          label="详情"
        />
        <el-table-column
          prop="amount"
          label="金额"
          width="120"
        >
          <template #default="{ row }">
            <span :class="getAmountClass(row.amount)">¥{{ formatAmount(row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="balance"
          label="余额"
          width="120"
        >
          <template #default="{ row }">
            ¥{{ formatAmount(row.balance) }}
          </template>
        </el-table-column>
        <el-table-column label="申诉" width="140" fixed="right">
          <template #default="{ row }">
            <template v-if="row.payType === 'Usage' && (row.canAppeal || row.appealStatus)">
              <el-button
                v-if="row.canAppeal"
                link
                type="warning"
                @click="openAppealDialog(row)"
              >
                申诉退款
              </el-button>
              <el-button
                v-else-if="row.appealStatus === 'need_info' && isWithinAppealDeadline(row)"
                link
                type="primary"
                @click="openSupplementDialog(row)"
              >
                补充材料
              </el-button>
              <el-tag v-else-if="row.appealStatus" size="small" :type="appealTagType(row.appealStatus)">
                {{ appealStatusLabel(row.appealStatus) }}
              </el-tag>
            </template>
            <span v-else>—</span>
          </template>
        </el-table-column>
      </el-table>

      <el-dialog v-model="appealVisible" title="申诉退款" width="520px" destroy-on-close>
        <p v-if="appealOrderNo" class="dialog-order">订单号：{{ appealOrderNo }}</p>
        <el-form label-width="80px">
          <el-form-item label="申诉理由" required>
            <el-input v-model="appealForm.reason" type="textarea" :rows="4" maxlength="500" show-word-limit />
          </el-form-item>
          <el-form-item label="凭证图片">
            <el-upload
              action="/api/v1/upload"
              list-type="picture-card"
              :headers="uploadHeaders"
              :file-list="appealFileList"
              :on-success="onAppealUploadSuccess"
              :on-remove="onAppealUploadRemove"
              :before-upload="beforeAppealUpload"
              accept="image/*"
            >
              <el-icon><Plus /></el-icon>
            </el-upload>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="appealVisible = false">取消</el-button>
          <el-button type="primary" :loading="appealSubmitting" @click="submitAppeal">提交申诉</el-button>
        </template>
      </el-dialog>

      <el-dialog v-model="supplementVisible" title="补充申诉材料" width="520px" destroy-on-close>
        <p v-if="supplementOrderNo" class="dialog-order">订单号：{{ supplementOrderNo }}</p>
        <el-alert v-if="supplementAdminReply" type="info" :closable="false" class="mb-3">
          管理员要求：{{ supplementAdminReply }}
        </el-alert>
        <el-form label-width="80px">
          <el-form-item label="补充说明">
            <el-input v-model="supplementForm.reason" type="textarea" :rows="3" maxlength="500" show-word-limit />
          </el-form-item>
          <el-form-item label="凭证图片">
            <el-upload
              action="/api/v1/upload"
              list-type="picture-card"
              :headers="uploadHeaders"
              :file-list="supplementFileList"
              :on-success="onSupplementUploadSuccess"
              :on-remove="onSupplementUploadRemove"
              :before-upload="beforeAppealUpload"
              accept="image/*"
            >
              <el-icon><Plus /></el-icon>
            </el-upload>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="supplementVisible = false">取消</el-button>
          <el-button type="primary" :loading="supplementSubmitting" @click="submitSupplement">提交</el-button>
        </template>
      </el-dialog>

      <AdminPagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import { goToLogin } from '@/utils/loginFlow'
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { Search, Plus } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'
import AdminPagination from '@/components/AdminPagination.vue'
import { adminUploadHeaders, uploadResponseToUrl, validatePhotoUpload } from '@/utils/photoUpload'
import {
  appealStatusLabel,
  fetchPurchaseRefundAppeal,
  fetchPurchaseRefundConfig,
  submitPurchaseRefundAppeal,
  supplementPurchaseRefundAppeal
} from '@/utils/purchaseRefund'

export default {
  name: 'TransactionListView',
  components: {
    Search,
    Plus,
    AdminPagination
  },
  setup() {
    const transactions = ref([])
    const total = ref(0)
    const currentPage = ref(1)
    const pageSize = ref(20)
    const loading = ref(false)
    const refundConfig = ref({ appealDays: 3, complaintPhone: '' })
    const filters = ref({
      transType: 0,
      orderNo: '',
      currencyCode: 'CNY',
      beginCreatedAt: 0,
      endCreatedAt: 0,
      orgId: 0
    })

    const appealVisible = ref(false)
    const appealSubmitting = ref(false)
    const appealOrderNo = ref('')
    const appealForm = ref({ reason: '', imageUrls: [] })
    const appealFileList = ref([])

    const supplementVisible = ref(false)
    const supplementSubmitting = ref(false)
    const supplementOrderNo = ref('')
    const supplementAppealId = ref(0)
    const supplementAdminReply = ref('')
    const supplementForm = ref({ reason: '', imageUrls: [] })
    const supplementFileList = ref([])

    const router = useRouter()
    const uploadHeaders = computed(() => adminUploadHeaders())

    const checkLogin = () => {
      if (!localStorage.getItem('jwt_token')) {
        goToLogin(router)
        return false
      }
      return true
    }

    const loadRefundConfig = async () => {
      try {
        refundConfig.value = await fetchPurchaseRefundConfig()
      } catch {
        refundConfig.value = { appealDays: 3, complaintPhone: '400-888-0000' }
      }
    }

    const loadTransactions = async () => {
      try {
        loading.value = true
        const token = localStorage.getItem('jwt_token')
        const response = await axios.post('/api/v1/transactions/list', {
          page: currentPage.value,
          pageSize: pageSize.value,
          ...filters.value
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.code === 200) {
          transactions.value = response.data.data.trans
          total.value = response.data.data.total
        } else {
          ElMessage.error(response.data.message || '获取交易记录失败')
        }
      } catch (error) {
        console.error('Failed to load transactions:', error)
        ElMessage.error('获取交易记录失败')
      } finally {
        loading.value = false
      }
    }

    const isWithinAppealDeadline = (row) => {
      if (!row?.appealDeadlineAt) return false
      return row.appealDeadlineAt * 1000 >= Date.now()
    }

    const appealTagType = (status) => {
      const map = { pending: 'warning', need_info: 'info', approved: 'success', rejected: 'danger' }
      return map[status] || 'info'
    }

    const resetAppealForm = () => {
      appealForm.value = { reason: '', imageUrls: [] }
      appealFileList.value = []
    }

    const openAppealDialog = (row) => {
      appealOrderNo.value = row.orderNo
      resetAppealForm()
      appealVisible.value = true
    }

    const beforeAppealUpload = (file) => validatePhotoUpload(file, ElMessage.error)

    const onAppealUploadSuccess = (response, file, fileList) => {
      const url = uploadResponseToUrl(response)
      if (url) {
        appealForm.value.imageUrls.push(url)
      }
      appealFileList.value = fileList
    }

    const onAppealUploadRemove = (file, fileList) => {
      const url = file.response ? uploadResponseToUrl(file.response) : file.url
      appealForm.value.imageUrls = appealForm.value.imageUrls.filter(u => u !== url)
      appealFileList.value = fileList
    }

    const submitAppeal = async () => {
      const reason = appealForm.value.reason.trim()
      if (!reason) {
        ElMessage.warning('请填写申诉理由')
        return
      }
      appealSubmitting.value = true
      try {
        await submitPurchaseRefundAppeal({
          orderNo: appealOrderNo.value,
          reason,
          imageUrls: appealForm.value.imageUrls
        })
        ElMessage.success('申诉已提交，请等待审核')
        appealVisible.value = false
        loadTransactions()
      } catch (e) {
        ElMessage.error(e.message || '提交失败')
      } finally {
        appealSubmitting.value = false
      }
    }

    const openSupplementDialog = async (row) => {
      supplementOrderNo.value = row.orderNo
      supplementForm.value = { reason: '', imageUrls: [] }
      supplementFileList.value = []
      supplementAdminReply.value = ''
      supplementAppealId.value = 0
      try {
        const data = await fetchPurchaseRefundAppeal(row.orderNo)
        supplementAppealId.value = data?.id || 0
        supplementAdminReply.value = data?.adminReply || ''
      } catch (e) {
        ElMessage.error(e.message || '加载申诉信息失败')
        return
      }
      supplementVisible.value = true
    }

    const onSupplementUploadSuccess = (response, file, fileList) => {
      const url = uploadResponseToUrl(response)
      if (url) {
        supplementForm.value.imageUrls.push(url)
      }
      supplementFileList.value = fileList
    }

    const onSupplementUploadRemove = (file, fileList) => {
      const url = file.response ? uploadResponseToUrl(file.response) : file.url
      supplementForm.value.imageUrls = supplementForm.value.imageUrls.filter(u => u !== url)
      supplementFileList.value = fileList
    }

    const submitSupplement = async () => {
      if (!supplementAppealId.value) {
        ElMessage.error('申诉记录无效')
        return
      }
      supplementSubmitting.value = true
      try {
        await supplementPurchaseRefundAppeal({
          appealId: supplementAppealId.value,
          reason: supplementForm.value.reason.trim(),
          imageUrls: supplementForm.value.imageUrls
        })
        ElMessage.success('材料已提交')
        supplementVisible.value = false
        loadTransactions()
      } catch (e) {
        ElMessage.error(e.message || '提交失败')
      } finally {
        supplementSubmitting.value = false
      }
    }

    const handlePageChange = (page) => {
      currentPage.value = page
      loadTransactions()
    }

    const handleSizeChange = (size) => {
      pageSize.value = size
      currentPage.value = 1
      loadTransactions()
    }

    const formatDate = (timestamp) => {
      return new Date(timestamp * 1000).toLocaleString()
    }

    const formatAmount = (amount) => {
      if (!amount && amount !== 0) return '0.00'
      return (Number(amount) / 100).toFixed(2)
    }

    const getTypeTagType = (type) => {
      const types = {
        'Recharge': 'success',
        'Usage': 'primary',
        'Gift': 'warning'
      }
      return types[type] || 'info'
    }

    const getAmountClass = (amount) => {
      return amount > 0 ? 'text-success' : 'text-danger'
    }

    onMounted(() => {
      if (!checkLogin()) {
        return
      }
      loadRefundConfig()
      loadTransactions()
    })

    return {
      transactions,
      total,
      currentPage,
      pageSize,
      loading,
      filters,
      refundConfig,
      appealVisible,
      appealSubmitting,
      appealOrderNo,
      appealForm,
      appealFileList,
      supplementVisible,
      supplementSubmitting,
      supplementOrderNo,
      supplementAdminReply,
      supplementForm,
      supplementFileList,
      uploadHeaders,
      loadTransactions,
      handlePageChange,
      handleSizeChange,
      formatDate,
      formatAmount,
      getTypeTagType,
      getAmountClass,
      appealStatusLabel,
      appealTagType,
      isWithinAppealDeadline,
      openAppealDialog,
      openSupplementDialog,
      submitAppeal,
      submitSupplement,
      beforeAppealUpload,
      onAppealUploadSuccess,
      onAppealUploadRemove,
      onSupplementUploadSuccess,
      onSupplementUploadRemove
    }
  }
}
</script>

<style scoped>
.transaction-list-container {
  padding: 20px;
  max-width: 1800px;
  margin: 0 auto;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.filters {
  margin-bottom: 20px;
}

.complaint-hint {
  margin: 0 0 16px;
  font-size: 14px;
  color: var(--el-text-color-secondary);
}

.complaint-hint a {
  color: var(--el-color-primary);
  text-decoration: none;
}

.dialog-order {
  margin: 0 0 12px;
  font-size: 14px;
  color: var(--el-text-color-regular);
}

.mb-3 {
  margin-bottom: 12px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.text-success {
  color: var(--el-color-success);
}

.text-danger {
  color: var(--el-color-danger);
}

:deep(.el-table) {
  --el-table-border-color: var(--el-border-color-lighter);
  --el-table-header-bg-color: var(--el-fill-color-light);
}

:deep(.el-table th) {
  font-weight: 600;
  color: var(--el-text-color-primary);
}

:deep(.el-table td) {
  padding: 12px 0;
}

:deep(.el-tag) {
  font-weight: 500;
}

@media (max-width: 768px) {
  .transaction-list-container {
    padding: 10px;
  }

  .card {
    padding: 15px;
  }
}

.filter-row {
  display: flex;
  gap: 16px;
  align-items: center;
}

.filter-item {
  flex: 1;
  max-width: 200px;
}

.filter-item:last-child {
  flex: 0 0 auto;
  max-width: none;
}
</style> 